import React, { useState, useEffect } from 'react';
import { loadTransplantCenters, getAllCenters, getCenterNameById } from '../utils/formatters';

function InputForm({ inputs, onInputChange, onCalculate, onReset, warningMessage, response, centerOnly = false }) {
  const [centers, setCenters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAdditionalParamsTooltip, setShowAdditionalParamsTooltip] = useState(false);

  useEffect(() => {
    const loadCenters = async () => {
      // Wait for centers data to load
      await loadTransplantCenters();
      const allCenters = getAllCenters();
      setCenters(allCenters);
    };
    loadCenters();
  }, []);

  // Sync search term with center name when centerId changes externally
  useEffect(() => {
    if (inputs.centerId && inputs.centerName) {
      setSearchTerm(inputs.centerName);
    } else if (inputs.centerId) {
      const name = getCenterNameById(inputs.centerId);
      if (name) {
        setSearchTerm(name);
      }
    } else {
      setSearchTerm('');
    }
  }, [inputs.centerId, inputs.centerName]);

  const handleCenterSearch = (value) => {
    setSearchTerm(value);
    setShowDropdown(true);
    
    // Only auto-update centerId if we have a valid 4-letter code
    if (value.length === 4 && /^[A-Z]{4}$/i.test(value)) {
      const upperValue = value.toUpperCase();
      // Check if this center ID exists
      const name = getCenterNameById(upperValue);
      if (name) {
        onInputChange('centerId', upperValue);
      }
    } else if (!value) {
      // Clear center ID when search is cleared
      onInputChange('centerId', '');
    }
    // For partial searches, don't update centerId until they select from dropdown
  };

  const handleCenterSelect = (center) => {
    setSearchTerm(center.name);
    setShowDropdown(false);
    onInputChange('centerId', center.id);
    
    // Track transplant center selection in PostHog
    if (window.posthog) {
      window.posthog.capture('transplant_center_selected', {
        center_id: center.id,
        center_name: center.name
      });
    }
  };

  const filteredCenters = searchTerm 
    ? centers.filter(center => 
        center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        center.id.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 10)
    : centers.slice(0, 50); // Show first 50 centers when no search term

  // Slider configuration constants
  const SLIDER_CONFIG = {
    transplants: {
      maxMultiplier: 1.5,  // Max is 150% of target
      minMultiplier: 0.75, // Min is 75% of min(current, target)
      defaultFallbackMax: 1000,
      defaultFallbackMin: 1,
    },
    offerAcceptance: {
      maxMultiplier: 1.5,  // Max is 150% of current
      minMultiplier: 0.5,  // Min is 50% of min(current, target)
      absoluteMax: 3,      // Cannot exceed 3
      defaultFallbackMax: 3,
      defaultFallbackMin: 0
    },
    graftSurvival: {
      maxMultiplier: 1.05, // Max is 105% of current
      minMultiplier: 0.95, // Min is 95% of min(current, target)
      absoluteMax: 100,    // Cannot exceed 100%
      defaultFallbackMax: 100,
      defaultFallbackMin: 0
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} style={{ position: 'relative' }}>
      {/* Only show center selection in centerOnly mode */}
      {centerOnly && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: centerOnly ? 'center' : 'flex-start', gap: '0.5rem', marginBottom: '1rem' }}>
          <div style={{ width: '100%', maxWidth: centerOnly ? '100%' : '500px', textAlign: 'left', position: 'relative' }}>
            {!centerOnly && (
              <label htmlFor="centerSearch" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem'}}>Transplant Center</label>
            )}
            <input 
              id="centerSearch" 
              type="text" 
              placeholder="Search by name or enter ID (e.g., CASF)" 
              value={searchTerm}
              onChange={(e) => handleCenterSearch(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              style={{ 
                width: '100%',
                fontSize: centerOnly ? '1.1rem' : '1rem',
                padding: centerOnly ? '1rem' : '0.75rem',
                textAlign: 'left',
                border: centerOnly ? '2px solid rgba(14,79,86,0.2)' : '1px solid rgba(0,0,0,0.15)',
                borderRadius: centerOnly ? '12px' : '8px',
                background: '#ffffff',
                boxShadow: centerOnly ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s ease'
              }}
            />
            
            {showDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                maxHeight: '300px',
                overflowY: 'auto',
                background: '#ffffff',
                border: '1px solid rgba(14,79,86,0.3)',
                borderRadius: '8px',
                marginTop: '4px',
                zIndex: 1000,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(8px)'
              }}>
                {filteredCenters.length > 0 ? (
                  <>
                    {searchTerm && (
                      <div style={{
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.75rem',
                        color: 'rgba(0,0,0,0.5)',
                        borderBottom: '1px solid rgba(0,0,0,0.08)'
                      }}>
                        {filteredCenters.length} result{filteredCenters.length !== 1 ? 's' : ''} found
                      </div>
                    )}
                    {filteredCenters.map(center => (
                      <div
                        key={center.id}
                        onClick={() => handleCenterSelect(center)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(0,0,0,0.08)',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(14,79,86,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: 'rgba(0,0,0,0.9)' }}>
                          {center.name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#0e4f56', marginTop: '2px' }}>
                          ID: {center.id}
                        </div>
                      </div>
                    ))}
                  </>
                ) : searchTerm ? (
                  <div style={{
                    padding: '1rem',
                    textAlign: 'center',
                    color: 'rgba(0,0,0,0.5)',
                    fontSize: '0.9rem'
                  }}>
                    No centers found matching "{searchTerm}"
                  </div>
                ) : (
                  <div style={{
                    padding: '1rem',
                    textAlign: 'center',
                    color: 'rgba(0,0,0,0.5)',
                    fontSize: '0.9rem'
                  }}>
                    Loading centers...
                  </div>
                )}
              </div>
            )}
            
            {inputs.centerId && (
              <div style={{ marginTop: '8px', fontSize: '0.9rem', color: 'rgba(0,0,0,0.6)' }}>
                Center ID: <span style={{ fontWeight: '700', letterSpacing: '0.1em', color: '#0e4f56' }}>{inputs.centerId}</span>
              </div>
            )}
          </div>
          {warningMessage ? (
            <p style={{ 
              margin: 0, 
              fontSize: '1rem', 
              fontWeight: '600',
              color: '#dc2626',
              textAlign: 'center'
            }}>
              ⚠️ {warningMessage}
            </p>
          ) : null}
        </div>
      )}

      {/* Only show the rest of the form if not in centerOnly mode */}
      {!centerOnly && (
        <>
      {/* Parameters Section - Always Expanded */}
      {inputs.centerId && response?.scores?.centerTransplants > 0 && response?.transplantVolume?.target > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div 
            style={{ 
              fontSize: '18px',
              fontWeight: '700',
              color: '#0e4f56',
              marginBottom: '8px',
              padding: '0'
            }}
          >
            Adjustable Parameters
          </div>
          <p style={{ 
            margin: '0 0 16px 0', 
            fontSize: '0.8rem', 
            color: 'rgba(0,0,0,0.6)',
            lineHeight: '1.4'
          }}>
            Input what metrics your center is expected to reach by July 2026
          </p>
          
          {/* Full sliders and additional params - Always Visible */}
          <div>
      {/* Expected Transplants Slider */}
      {inputs.centerId && response?.scores?.centerTransplants > 0 && response?.transplantVolume?.target > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="numTransplants" style={{ fontSize: '0.9rem' }}>Expected Transplants (annual)</label>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input 
                  id="numTransplants" 
                  type="range" 
                  min={(() => {
                    const centerTransplants = response?.scores?.centerTransplants;
                    const targetTransplants = response?.transplantVolume?.target;
                    if (centerTransplants && targetTransplants && centerTransplants > 0 && targetTransplants > 0) {
                      return Math.max(SLIDER_CONFIG.transplants.defaultFallbackMin, Math.round(Math.min(centerTransplants, targetTransplants) * SLIDER_CONFIG.transplants.minMultiplier));
                    }
                    return SLIDER_CONFIG.transplants.defaultFallbackMin;
                  })()}
                  max={(() => {
                    const centerTransplants = response?.scores?.centerTransplants;
                    const targetTransplants = response?.transplantVolume?.target;
                    if (centerTransplants && centerTransplants > 0 && targetTransplants && targetTransplants > 0) {
                      return Math.round(targetTransplants * SLIDER_CONFIG.transplants.maxMultiplier);
                    }
                    return SLIDER_CONFIG.transplants.defaultFallbackMax;
                  })()}
                  step="1" 
                  value={inputs.numTransplants}
                onChange={(e) => onInputChange('numTransplants', e.target.value)}
                style={{ 
                  width: '100%',
                  background: (() => {
                    if (response?.transplantVolume?.target && response.transplantVolume.target > 0 && response?.scores?.centerTransplants && response.scores.centerTransplants > 0 && response?.maximizeScores?.achievement && response.maximizeScores.achievement > 0) {
                      const optimalTransplants = response.maximizeScores.achievement;
                      const centerTransplants = response.scores.centerTransplants;
                      const targetTransplants = response.transplantVolume.target;
                      const max = Math.round(targetTransplants * SLIDER_CONFIG.transplants.maxMultiplier);
                      const min = Math.max(SLIDER_CONFIG.transplants.defaultFallbackMin, Math.round(Math.min(centerTransplants, targetTransplants) * SLIDER_CONFIG.transplants.minMultiplier));
                      const optimalPercent = ((optimalTransplants - min) / (max - min)) * 100;
                      return `linear-gradient(to right, #ddd 0%, #ddd ${optimalPercent}%, #0e4f56 ${optimalPercent}%, #0e4f56 100%)`;
                    }
                    return '#ddd';
                  })()
                }}
              />
              {response?.scores?.centerTransplants && response.scores.centerTransplants > 0 && response?.transplantVolume?.target && response.transplantVolume.target > 0 && (
                <div 
                  style={{ 
                    position: 'absolute',
                    left: `${(() => {
                      const centerTransplants = response.scores.centerTransplants;
                      const targetTransplants = response.transplantVolume.target;
                      const max = Math.round(targetTransplants * SLIDER_CONFIG.transplants.maxMultiplier);
                      const min = Math.max(SLIDER_CONFIG.transplants.defaultFallbackMin, Math.round(Math.min(centerTransplants, targetTransplants) * SLIDER_CONFIG.transplants.minMultiplier));
                      return ((centerTransplants - min) / (max - min)) * 100;
                    })()}%`,
                    top: '-10px',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                    color: '#0e4f56',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}
                >
                  Current: {Math.round(response.scores.centerTransplants)}
                </div>
              )}
              {response?.scores?.centerTransplants && response.scores.centerTransplants > 0 && response?.transplantVolume?.target && response.transplantVolume.target > 0 && (
                <div 
                  style={{ 
                    position: 'absolute',
                    left: `${(() => {
                      const centerTransplants = response.scores.centerTransplants;
                      const targetTransplants = response.transplantVolume.target;
                      const max = Math.round(targetTransplants * SLIDER_CONFIG.transplants.maxMultiplier);
                      const min = Math.max(SLIDER_CONFIG.transplants.defaultFallbackMin, Math.round(Math.min(centerTransplants, targetTransplants) * SLIDER_CONFIG.transplants.minMultiplier));
                      return ((centerTransplants - min) / (max - min)) * 100;
                    })()}%`,
                    top: '20',
                    bottom: '0',
                    width: '2px',
                    height: '12px',
                    backgroundColor: '#0e4f56',
                    pointerEvents: 'none',
                    opacity: 0.7,
                    zIndex: 1
                  }}
                />
              )}
              {response?.transplantVolume?.target && response.transplantVolume.target > 0 && response?.scores?.centerTransplants && response.scores.centerTransplants > 0 && response?.maximizeScores?.achievement && response.maximizeScores.achievement > 0 && (
                <>
                  <div 
                    style={{ 
                      position: 'absolute',
                      right: '0',
                      top: '20px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.8rem',
                      color: '#0e4f56',
                      fontWeight: '600',
                      pointerEvents: 'none',
                      textAlign: 'right'
                    }}
                  >
                    Target: {response.maximizeScores.achievement}
                  </div>
                </>
              )}
            </div>
              <input 
                type="number" 
                min={(() => {
                  const centerTransplants = response?.scores?.centerTransplants;
                  const targetTransplants = response?.transplantVolume?.target;
                  if (centerTransplants && targetTransplants && centerTransplants > 0 && targetTransplants > 0) {
                    return Math.max(SLIDER_CONFIG.transplants.defaultFallbackMin, Math.round(Math.min(centerTransplants, targetTransplants) * SLIDER_CONFIG.transplants.minMultiplier));
                  }
                  return SLIDER_CONFIG.transplants.defaultFallbackMin;
                })()}
                max={(() => {
                  const centerTransplants = response?.scores?.centerTransplants;
                  const targetTransplants = response?.transplantVolume?.target;
                  if (centerTransplants && centerTransplants > 0 && targetTransplants && targetTransplants > 0) {
                    return Math.round(targetTransplants * SLIDER_CONFIG.transplants.maxMultiplier);
                  }
                  return SLIDER_CONFIG.transplants.defaultFallbackMax;
                })()}
                step="1" 
                value={inputs.numTransplants}
                onChange={(e) => onInputChange('numTransplants', e.target.value)}
                style={{ width: '60px', textAlign: 'center' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Offer Acceptance Rate Slider */}
      {inputs.centerId && response?.scores?.centerOfferAcceptRate > 0 && response?.distribution?.acceptance?.percentiles?.p80 > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="offerAcceptRate" style={{ fontSize: '0.9rem' }}>Offer Acceptance Rate</label>
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input 
                id="offerAcceptRate" 
                type="range" 
                min={(() => {
                  const centerRate = response?.scores?.centerOfferAcceptRate;
                  const targetRate = response?.distribution?.acceptance?.percentiles?.p80;
                  if (centerRate && targetRate && centerRate > 0 && targetRate > 0) {
                    return Math.max(SLIDER_CONFIG.offerAcceptance.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.offerAcceptance.minMultiplier) * 100) / 100);
                  }
                  return SLIDER_CONFIG.offerAcceptance.defaultFallbackMin;
                })()}
                max={(() => {
                  const targetRate = response?.distribution?.acceptance?.percentiles?.p80;
                  if (targetRate && targetRate > 0) {
                    return Math.round((targetRate * 1.25) * 100) / 100;
                  }
                  return SLIDER_CONFIG.offerAcceptance.defaultFallbackMax;
                })()}
                step="0.01" 
                value={inputs.offerAcceptRate}
                onChange={(e) => onInputChange('offerAcceptRate', e.target.value)}
                style={{ 
                  width: '100%',
                  background: (() => {
                    if (response?.distribution?.acceptance?.percentiles?.p80 && response.distribution.acceptance.percentiles.p80 > 0 && response?.scores?.centerOfferAcceptRate && response.scores.centerOfferAcceptRate > 0) {
                      const targetRate = response.distribution.acceptance.percentiles.p80;
                      const centerRate = response.scores.centerOfferAcceptRate;
                      const max = Math.round((targetRate * 1.25) * 100) / 100;
                      const min = Math.max(SLIDER_CONFIG.offerAcceptance.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.offerAcceptance.minMultiplier) * 100) / 100);
                      const targetPercent = ((targetRate - min) / (max - min)) * 100;
                      return `linear-gradient(to right, #ddd 0%, #ddd ${targetPercent}%, #0e4f56 ${targetPercent}%, #0e4f56 100%)`;
                    }
                    return '#ddd';
                  })()
                }}
              />
              {response?.scores?.centerOfferAcceptRate && response.scores.centerOfferAcceptRate > 0 && response?.distribution?.acceptance?.percentiles?.p80 && response.distribution.acceptance.percentiles.p80 > 0 && (
                <div 
                  style={{ 
                    position: 'absolute',
                    left: `${(() => {
                      const centerRate = response.scores.centerOfferAcceptRate;
                      const targetRate = response.distribution.acceptance.percentiles.p80;
                      const max = Math.round((targetRate * 1.25) * 100) / 100;
                      const min = Math.max(SLIDER_CONFIG.offerAcceptance.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.offerAcceptance.minMultiplier) * 100) / 100);
                      return ((centerRate - min) / (max - min)) * 100;
                    })()}%`,
                    top: '-10px',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                    color: '#0e4f56',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}
                >
                  Current: {response.scores.centerOfferAcceptRate.toFixed(2)}
                </div>
              )}
              {response?.scores?.centerOfferAcceptRate && response.scores.centerOfferAcceptRate > 0 && response?.distribution?.acceptance?.percentiles?.p80 && response.distribution.acceptance.percentiles.p80 > 0 && (
                <div 
                  style={{ 
                    position: 'absolute',
                    left: `${(() => {
                      const centerRate = response.scores.centerOfferAcceptRate;
                      const targetRate = response.distribution.acceptance.percentiles.p80;
                      const max = Math.round((targetRate * 1.25) * 100) / 100;
                      const min = Math.max(SLIDER_CONFIG.offerAcceptance.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.offerAcceptance.minMultiplier) * 100) / 100);
                      return ((centerRate - min) / (max - min)) * 100;
                    })()}%`,
                    top: '20',
                    bottom: '0',
                    width: '2px',
                    height: '12px',
                    backgroundColor: '#0e4f56',
                    pointerEvents: 'none',
                    opacity: 0.7,
                    zIndex: 1
                  }}
                />
              )}
              {response?.distribution?.acceptance?.percentiles?.p80 && response.distribution.acceptance.percentiles.p80 > 0 && response?.scores?.centerOfferAcceptRate && response.scores.centerOfferAcceptRate > 0 && response?.maximizeScores?.efficiency && response.maximizeScores.efficiency > 0 && (
                <>
                  <div 
                    style={{ 
                      position: 'absolute',
                      right: '0',
                      top: '20px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.8rem',
                      color: '#0e4f56',
                      fontWeight: '600',
                      pointerEvents: 'none',
                      textAlign: 'right'
                    }}
                  >
                    Target: {response.maximizeScores.efficiency}
                  </div>
                </>
              )}
            </div>
              <input 
                type="number" 
                value={inputs.offerAcceptRate}
                onChange={(e) => onInputChange('offerAcceptRate', e.target.value)}
                style={{ width: '60px', textAlign: 'center' }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Graft Survival Rate Slider */}
      {inputs.centerId && response?.scores?.centerGraftSurvival > 0 && response?.distribution?.graftSurvival?.percentiles?.p80 > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="graftSurvival" style={{ fontSize: '0.9rem' }}>Graft Survival Rate (1yr %)</label>
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input 
                id="graftSurvival" 
                type="range" 
                min={(() => {
                  const centerRate = response?.scores?.centerGraftSurvival;
                  const targetRate = response?.distribution?.graftSurvival?.percentiles?.p80;
                  if (centerRate && targetRate) {
                    return Math.max(SLIDER_CONFIG.graftSurvival.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.minMultiplier) * 10) / 10);
                  }
                  return SLIDER_CONFIG.graftSurvival.defaultFallbackMin;
                })()}
                max={(() => {
                  const centerRate = response?.scores?.centerGraftSurvival;
                  const targetRate = response?.distribution?.graftSurvival?.percentiles?.p80;
                  if (centerRate && centerRate > 0 && targetRate) {
                    // When center > target, use center for max; when target > center, use target
                    return Math.min(SLIDER_CONFIG.graftSurvival.absoluteMax, Math.round((Math.max(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.maxMultiplier) * 10) / 10);
                  } else if (centerRate && centerRate > 0) {
                    return Math.min(SLIDER_CONFIG.graftSurvival.absoluteMax, Math.round((centerRate * SLIDER_CONFIG.graftSurvival.maxMultiplier) * 10) / 10);
                  }
                  return SLIDER_CONFIG.graftSurvival.defaultFallbackMax;
                })()}
                step="0.1" 
                value={inputs.graftSurvival}
                onChange={(e) => onInputChange('graftSurvival', e.target.value)}
                style={{ 
                  width: '100%',
                  background: (() => {
                    if (response?.distribution?.graftSurvival?.percentiles?.p80 && response.distribution.graftSurvival.percentiles.p80 > 0 && response?.scores?.centerGraftSurvival && response.scores.centerGraftSurvival > 0) {
                      const targetRate = response.distribution.graftSurvival.percentiles.p80;
                      const centerRate = response.scores.centerGraftSurvival;
                      // When center > target, use center for max; when target > center, use target
                      const max = Math.min(SLIDER_CONFIG.graftSurvival.absoluteMax, Math.round((Math.max(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.maxMultiplier) * 10) / 10);
                      const min = Math.max(SLIDER_CONFIG.graftSurvival.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.minMultiplier) * 10) / 10);
                      const targetPercent = ((targetRate - min) / (max - min)) * 100;
                      return `linear-gradient(to right, #ddd 0%, #ddd ${targetPercent}%, #0e4f56 ${targetPercent}%, #0e4f56 100%)`;
                    }
                    return '#ddd';
                  })()
                }}
              />
              {response?.scores?.centerGraftSurvival != null && response.scores.centerGraftSurvival > 0 && response?.distribution?.graftSurvival?.percentiles?.p80 && response.distribution.graftSurvival.percentiles.p80 > 0 && (
                <div 
                  style={{ 
                    position: 'absolute',
                    left: `${(() => {
                      const centerRate = response.scores.centerGraftSurvival;
                      const targetRate = response.distribution.graftSurvival.percentiles.p80;
                      // When center > target, use center for max; when target > center, use target
                      const max = Math.min(SLIDER_CONFIG.graftSurvival.absoluteMax, Math.round((Math.max(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.maxMultiplier) * 10) / 10);
                      const min = Math.max(SLIDER_CONFIG.graftSurvival.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.minMultiplier) * 10) / 10);
                      
                      return ((centerRate - min) / (max - min)) * 100;
                    })()}%`,
                    top: '-10px',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                    color: '#0e4f56',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}
                >
                  Current: {response.scores.centerGraftSurvival.toFixed(1)}%
                </div>
              )}
              {response?.scores?.centerGraftSurvival != null && response.scores.centerGraftSurvival > 0 && response?.distribution?.graftSurvival?.percentiles?.p80 && response.distribution.graftSurvival.percentiles.p80 > 0 && (
                <div 
                  style={{ 
                    position: 'absolute',
                    left: `${(() => {
                      const centerRate = response.scores.centerGraftSurvival;
                      const targetRate = response.distribution.graftSurvival.percentiles.p80;
                      // When center > target, use center for max; when target > center, use target
                      const max = Math.min(SLIDER_CONFIG.graftSurvival.absoluteMax, Math.round((Math.max(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.maxMultiplier) * 10) / 10);
                      const min = Math.max(SLIDER_CONFIG.graftSurvival.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.minMultiplier) * 10) / 10);
                      return ((centerRate - min) / (max - min)) * 100;
                    })()}%`,
                    top: '20',
                    bottom: '0',
                    width: '2px',
                    height: '12px',
                    backgroundColor: '#0e4f56',
                    pointerEvents: 'none',
                    opacity: 0.7,
                    zIndex: 1
                  }}
                />
              )}
              {response?.distribution?.graftSurvival?.percentiles?.p80 && response.distribution.graftSurvival.percentiles.p80 > 0 && response?.scores?.centerGraftSurvival && response.scores.centerGraftSurvival > 0 && response?.maximizeScores?.quality && response.maximizeScores.quality > 0 && (
                <>
                  <div 
                    style={{ 
                      position: 'absolute',
                      right: '0',
                      top: '20px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.8rem',
                      color: '#0e4f56',
                      fontWeight: '600',
                      pointerEvents: 'none',
                      textAlign: 'right'
                    }}
                  >
                    Target: {response.maximizeScores.quality}%
                  </div>
                </>
              )}
            </div>
              <input 
                type="number" 
                min={(() => {
                  const centerRate = response?.scores?.centerGraftSurvival;
                  const targetRate = response?.distribution?.graftSurvival?.percentiles?.p80;
                  if (centerRate && targetRate) {
                    return Math.max(SLIDER_CONFIG.graftSurvival.defaultFallbackMin, Math.round((Math.min(centerRate, targetRate) * SLIDER_CONFIG.graftSurvival.minMultiplier) * 10) / 10);
                  }
                  return SLIDER_CONFIG.graftSurvival.defaultFallbackMin;
                })()}
                max={(() => {
                  const centerRate = response?.scores?.centerGraftSurvival;
                  if (centerRate && centerRate > 0) {
                    return Math.min(SLIDER_CONFIG.graftSurvival.absoluteMax, Math.round((centerRate * SLIDER_CONFIG.graftSurvival.maxMultiplier) * 10) / 10);
                  }
                  return SLIDER_CONFIG.graftSurvival.defaultFallbackMax;
                })()}
                step="0.1" 
                value={inputs.graftSurvival}
                onChange={(e) => onInputChange('graftSurvival', e.target.value)}
                style={{ width: '60px', textAlign: 'center' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Additional Parameters */}
      {inputs.centerId && response?.scores?.centerTransplants > 0 && response?.transplantVolume?.target > 0 && (
      <div style={{ marginBottom: '1rem', position: 'relative' }}>
        <span
          onMouseEnter={(e) => {
            e.stopPropagation();
            setShowAdditionalParamsTooltip(true);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setShowAdditionalParamsTooltip(false);
          }}
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            border: '1.5px solid rgba(14,79,86,0.4)',
            color: 'rgba(14,79,86,0.7)',
            fontSize: '10px',
            fontWeight: '700',
            cursor: 'help',
            transition: 'all 0.2s ease',
            backgroundColor: showAdditionalParamsTooltip ? 'rgba(14,79,86,0.1)' : 'transparent',
            flexShrink: 0
          }}
        >
          i
        </span>
        {showAdditionalParamsTooltip && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              right: '0',
              marginBottom: '8px',
              backgroundColor: '#2a2a2a',
              color: '#ffffff',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              lineHeight: '1.5',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              pointerEvents: 'auto',
              minWidth: '250px',
              maxWidth: '400px',
              whiteSpace: 'normal'
            }}
          >
            The percentage of Medicare FFS is the national average and the percentage living donors is from the center's last SRTR report.
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: '12px',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid #2a2a2a'
              }}
            />
          </div>
        )}
        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          gap: '1rem'
        }}>
          <div>
            <label htmlFor="medicareFfsPercent" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              Medicare FFS Transplants (%)
            </label>
            <input 
              id="medicareFfsPercent" 
              type="number" 
              min="0" 
              max="100" 
              step="any" 
              value={inputs.medicareFfsPercent}
              onChange={(e) => onInputChange('medicareFfsPercent', e.target.value)}
              style={{ width: '120px' }}
            />
          </div>
          <div>
            <label htmlFor="livingDonorPercent" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              Living Donors (%)
            </label>
            <input 
              id="livingDonorPercent" 
              type="number" 
              min="0" 
              max="100" 
              step="any" 
              value={inputs.livingDonorPercent}
              onChange={(e) => onInputChange('livingDonorPercent', e.target.value)}
              style={{ width: '120px' }}
            />
          </div>
        </div>
      </div>
      )}
          </div>
          </div>
      )}

      <div className="divider"></div>

      {/* <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
        <button className="btn primary" onClick={onCalculate}>
          Run Model
        </button>
        <button className="btn" onClick={onReset}>
          Reset
        </button>
      </div> */}
        </>
      )}
    </form>
  );
}

export default InputForm;
